import { Button, Paper, Radio, Stack, Text, TextInput } from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { client } from "@/api-client";
import type { AdminBookingSettingsOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { PersonalLinkPreview } from "@/components/PersonalLinkPreview";
import { useTrainerProfileDisplay } from "@/hooks/useProfileDisplay";
import { UniversalRoutes, useNavigator } from "@/routers/routes";

type BookingSettings = AdminBookingSettingsOutput;

export const BookingSettings = () => {
  const { data: settings } = client.useQuery(
    "get",
    "/trainer/profile/booking",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!settings) {
    return <FancyLoader />;
  }

  return <BookingSettingsWhenLoaded settings={settings} />;
};

const whoCanBookOptions = [
  { label: "Everyone using search or link", value: "EVERYONE" },
  { label: "Only those who have your link", value: "WITH_LINK" },
  { label: "Clients who booked in the past", value: "EXISTING_CLIENTS" },
  { label: "Nobody (you are unavailable)", value: "NOBODY" },
];

const BookingSettingsWhenLoaded = (props: { settings: BookingSettings }) => {
  const { navigate } = useNavigator();

  // Fetch profile to get inviteCode
  const { profile } = useTrainerProfileDisplay();

  const updateMutation = client.useMutation("post", "/trainer/profile/booking");

  const form = useForm({
    initialValues: {
      ...props.settings,
      instantBooking: props.settings.instantBooking ? "true" : "false",
      personalLink: props.settings.personalLink || "",
    },
    validate: {
      whoCanBook: isNotEmpty("Who can book is required"),
      instantBooking: isNotEmpty("Instant booking is required"),
      // Optionally add validation for personalLink if needed
    },
    transformValues: (values) => ({
      ...values,
      instantBooking: values.instantBooking === "true",
      personalLink: values.personalLink,
    }),
  });

  const handleSubmit = async (values: BookingSettings) => {
    await updateMutation.mutateAsync({
      body: values,
    });
    navigate({ path: UniversalRoutes.PROFILE.HOME });
  };

  return (
    <Stack gap="xs">
      <Header title="Booking Settings" />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          <Stack gap="xs">
            <Text size={"14px"} fw={"500"}>
              Who can book a session
            </Text>
            <Radio.Group {...form.getInputProps("whoCanBook")}>
              <Stack gap="xs">
                {whoCanBookOptions.map((option) => (
                  <Paper key={option.value} withBorder p="md">
                    <Radio value={option.value} label={option.label} />
                  </Paper>
                ))}
              </Stack>
            </Radio.Group>
          </Stack>

          <Stack gap="xs">
            <Text size={"14px"} fw={"500"}>
              Session booking confirmation
            </Text>
            <Radio.Group {...form.getInputProps("instantBooking")}>
              <Stack gap="xs">
                <Paper withBorder p="md">
                  <Radio
                    value={"true"}
                    label="Automatic, clients requests are confirmed instantly."
                  />
                </Paper>
                <Paper withBorder p="md">
                  <Radio
                    value={"false"}
                    label="Manual, you will confirm all requests first."
                  />
                </Paper>
              </Stack>
            </Radio.Group>
          </Stack>

          <Stack gap="xs">
            <Text size={"14px"} fw={"500"}>
              Personal booking link customisation
            </Text>
            <TextInput
              label="Personalise your Woofapy link"
              placeholder="e.g. john-doe"
              key={form.key("personalLink")}
              {...form.getInputProps("personalLink")}
            />
            {profile && (
              <PersonalLinkPreview
                inviteCode={profile.inviteCode}
                personalLink={form.values.personalLink}
              />
            )}
          </Stack>

          <Button type="submit" fullWidth loading={updateMutation.isPending}>
            Save Changes
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};
